import { useEffect, useRef, useState, type ReactNode } from "react";
import { useLocation } from "react-router-dom";

interface PageTransitionProps {
  children: ReactNode;
}

export default function PageTransition({ children }: PageTransitionProps) {
  const location = useLocation();
  const [displayChildren, setDisplayChildren] = useState(children);
  const [phase, setPhase] = useState<"enter" | "idle">("idle");
  const isFirstRender = useRef(true);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      setDisplayChildren(children);
      // Trigger enter on first mount
      const raf = requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setPhase("enter");
        });
      });
      return () => cancelAnimationFrame(raf);
    }

    // Subsequent navigations: exit old → swap → enter new
    setPhase("idle");
    const swapTimer = setTimeout(() => {
      setDisplayChildren(children);
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setPhase("enter");
        });
      });
    }, 160);
    return () => clearTimeout(swapTimer);
  }, [location.pathname]);

  return (
    <div className={phase === "enter" ? "animate-page-enter" : ""}>
      {displayChildren}
    </div>
  );
}